/**
 * Inline Cart Module
 * Slide-in cart panel (full viewport height, slides in from the right).
 * Replaces the standalone ShoppingCart page for all tenants.
 *
 * Checkout integrity model:
 * - The cart is signed when the checkout page loads (that signature describes
 *   what the customer actually sees in the order summary).
 * - The signature is compared when the panel closes and on pageshow (persisted).
 * - If the cart changed, whitelisted form fields are backed up and the page is
 *   reloaded via location.href assignment (never location.reload(), to avoid
 *   POST resubmit dialogs). The panel NEVER live-updates the checkout summary.
 */

import { getStorage, setStorage, clearStorage } from '../core/storage.js';
import { escapeHtml, dispatchCartUpdatedEvent } from '../core/utils.js';
import { t } from '../core/i18n.js';

// sessionStorage key for the whitelisted checkout form backup (deleted immediately after restore)
const FORM_BACKUP_KEY = 'inlineCartCheckoutFormBackup';

// Whitelisted checkout form fields (address/contact data only).
// NEVER include consents (acceptTerms, newsletters), SSNs or card data.
const FORM_FIELD_WHITELIST = [
    'customerName', 'customerLastname', 'companyName', 'customerCountry',
    'customerAddress', 'customerAddress2', 'customerZip', 'customerCity',
    'customerPhone', 'customerEmail',
    'differentDeliveryAddress',
    'deliveryName', 'deliveryLastname', 'deliveryCompany',
    'deliveryAddress', 'deliveryAddress2', 'deliveryZip', 'deliveryCity'
];

export class InlineCart {
    constructor(system) {
        this.system = system;
        this.isOpen = false;
        this.isCheckout = false;
        this.pageLoadSignature = null;
        this.reloadTriggered = false;
        this.previouslyFocused = null;
        this.inertElements = [];
        this.savedScrollY = 0;
        this.backdrop = null;
        this.panel = null;
        this.itemsContainer = null;
        this.footerElement = null;
        this.emptyButton = null;
    }

    init() {
        // Checkout detection uses the same unique element as CustomerFormHandler
        this.isCheckout = !!document.getElementById('order-items-list');

        if (this.isCheckout) {
            // Sign the cart at page load - this describes what the customer sees
            this.pageLoadSignature = this.computeSignature();

            // Catch bfcache returns where the cart was changed on another page.
            // Only run on persisted pageshows - a regular load signs in the same tick.
            window.addEventListener('pageshow', (event) => {
                if (event.persisted) {
                    this.maybeReloadAfterCartChange('pageshow');
                }
            });

            // Catch cart changes made in ANOTHER tab (shared localStorage fires
            // 'storage' only in other tabs). The order summary must be re-initialized
            // via the same reload gate as local changes.
            window.addEventListener('storage', (event) => {
                if (event.key === this.system.config.storageKey) {
                    this.maybeReloadAfterCartChange('storage');
                }
            });

            // Restore whitelisted form fields if we are returning from a cart-change reload
            this.restoreFormFields();
        }

        this.createDOM();
        this.setupEventListeners();

        console.log('InlineCart: Initialized (checkout page:', this.isCheckout + ')');
    }

    // ------------------------------------------------------------------
    // DOM
    // ------------------------------------------------------------------

    createDOM() {
        this.backdrop = document.createElement('div');
        this.backdrop.className = 'inline-cart-backdrop';
        this.backdrop.hidden = true;

        this.panel = document.createElement('div');
        this.panel.className = 'inline-cart-panel';
        this.panel.setAttribute('role', 'dialog');
        this.panel.setAttribute('aria-modal', 'true');
        this.panel.setAttribute('aria-label', t('cart.title'));
        this.panel.hidden = true;

        this.panel.innerHTML = `
            <div class="inline-cart-header">
                <h2 class="inline-cart-title">${escapeHtml(t('cart.title'))}</h2>
                <button type="button" class="inline-cart-close js-inline-cart-close" aria-label="${escapeHtml(t('cart.close'))}">&times;</button>
            </div>
            <div class="inline-cart-actions">
                <button type="button" class="inline-cart-empty js-inline-cart-empty">${escapeHtml(t('cart.emptyCart'))}</button>
            </div>
            <div class="inline-cart-items js-inline-cart-items"></div>
            <div class="inline-cart-footer js-inline-cart-footer"></div>
        `;

        document.body.appendChild(this.backdrop);
        document.body.appendChild(this.panel);

        this.itemsContainer = this.panel.querySelector('.js-inline-cart-items');
        this.footerElement = this.panel.querySelector('.js-inline-cart-footer');
        this.emptyButton = this.panel.querySelector('.js-inline-cart-empty');
    }

    setupEventListeners() {
        // Close triggers
        this.panel.querySelector('.js-inline-cart-close').addEventListener('click', () => this.close());
        this.backdrop.addEventListener('click', () => this.close());

        // ESC: capture phase + stopPropagation so search.js never sees it while the panel is open
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && this.isOpen) {
                e.stopPropagation();
                this.close();
            }
        }, true);

        // Focus trap
        this.panel.addEventListener('keydown', (e) => this.handleTabKey(e));

        // "Edit cart" button on the checkout page (delegated - element lives in CMS template content)
        document.addEventListener('click', (e) => {
            const editButton = e.target.closest('.js-edit-cart');
            if (editButton) {
                e.preventDefault();
                this.open(editButton);
            }
        });

        // Quantity controls and footer actions (delegated within the panel)
        this.panel.addEventListener('click', (e) => {
            const increase = e.target.closest('.js-increase');
            const decrease = e.target.closest('.js-decrease');
            if (increase) {
                this.increaseQuantity(increase.dataset.productId);
            } else if (decrease) {
                this.decreaseQuantity(decrease.dataset.productId);
            } else if (e.target.closest('.js-inline-cart-done')) {
                this.close();
            }
        });

        this.emptyButton.addEventListener('click', () => this.emptyCart());

        // Re-render while open when the cart changes (e.g. gift card added)
        document.addEventListener('cartUpdated', () => {
            if (this.isOpen) {
                this.renderItems();
                this.renderFooter();
            }
        });
    }

    // ------------------------------------------------------------------
    // Open / close
    // ------------------------------------------------------------------

    open(triggerElement = null) {
        if (this.isOpen) return;
        this.isOpen = true;

        // Hide an open search dropdown so only one overlay is visible
        const openSearchDropdown = document.querySelector('.search-dropdown');
        if (openSearchDropdown) {
            openSearchDropdown.style.display = 'none';
        }

        this.previouslyFocused = triggerElement || document.activeElement;

        this.renderItems();
        this.renderFooter();

        this.backdrop.hidden = false;
        this.panel.hidden = false;

        // Force reflow so the slide transition runs
        void this.panel.offsetWidth;
        this.backdrop.classList.add('is-visible');
        this.panel.classList.add('is-open');

        this.applyScrollLock();
        this.applyInert();

        const closeButton = this.panel.querySelector('.js-inline-cart-close');
        if (closeButton) closeButton.focus();
    }

    close() {
        if (!this.isOpen) return;
        this.isOpen = false;

        this.backdrop.classList.remove('is-visible');
        this.panel.classList.remove('is-open');

        this.releaseScrollLock();
        this.removeInert();

        // Wait for the slide-out transition before hiding (keeps focus valid meanwhile)
        setTimeout(() => {
            if (!this.isOpen) {
                this.backdrop.hidden = true;
                this.panel.hidden = true;
            }
        }, 350);

        this.restoreFocus();

        if (this.isCheckout) {
            this.maybeReloadAfterCartChange('panel-close');
        }
    }

    restoreFocus() {
        // Product buttons may have been re-rendered while the panel was open,
        // so only use the saved reference if it is still in the DOM
        if (this.previouslyFocused && this.previouslyFocused.isConnected) {
            this.previouslyFocused.focus();
        } else {
            const cartButton = document.querySelector('.js-cart');
            if (cartButton) cartButton.focus();
        }
        this.previouslyFocused = null;
    }

    // ------------------------------------------------------------------
    // Scroll lock (iOS-safe: save/restore scroll position to avoid page jump)
    // ------------------------------------------------------------------

    applyScrollLock() {
        this.savedScrollY = window.scrollY;
        document.body.classList.add('inline-cart-scroll-lock');
        document.body.style.top = `-${this.savedScrollY}px`;
    }

    releaseScrollLock() {
        document.body.classList.remove('inline-cart-scroll-lock');
        document.body.style.top = '';
        window.scrollTo(0, this.savedScrollY);
    }

    // ------------------------------------------------------------------
    // Inert / aria-hidden on background (panel is appended to body,
    // so body itself cannot be inert - iterate its children instead)
    // ------------------------------------------------------------------

    applyInert() {
        this.inertElements = [];
        Array.from(document.body.children).forEach((el) => {
            if (el === this.panel || el === this.backdrop) return;
            // inert is unsupported in older Safari - the focus trap is the real
            // barrier there, so this degrades gracefully
            if ('inert' in el) el.inert = true;
            el.setAttribute('aria-hidden', 'true');
            this.inertElements.push(el);
        });
    }

    removeInert() {
        this.inertElements.forEach((el) => {
            if ('inert' in el) el.inert = false;
            el.removeAttribute('aria-hidden');
        });
        this.inertElements = [];
    }

    handleTabKey(e) {
        if (e.key !== 'Tab' || !this.isOpen) return;

        const focusable = this.panel.querySelectorAll(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (focusable.length === 0) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (e.shiftKey && document.activeElement === first) {
            e.preventDefault();
            last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
            e.preventDefault();
            first.focus();
        }
    }


    // ------------------------------------------------------------------
    // Rendering
    // ------------------------------------------------------------------

    renderItems() {
        const items = getStorage(this.system.config.storageKey);
        this.itemsContainer.innerHTML = '';

        const visibleItems = items.filter(item =>
            item.Type !== 'Surcharge' && item.ProductNr !== 'PHYSICAL-CARD-FEE'
        );

        if (visibleItems.length === 0) {
            this.itemsContainer.innerHTML = `<p class="inline-cart-empty-message">${escapeHtml(t('cart.empty'))}</p>`;
            this.emptyButton.style.display = 'none';
            return;
        }

        this.emptyButton.style.display = 'block';

        visibleItems.forEach(item => {
            this.itemsContainer.appendChild(this.createItemElement(item));
        });
    }

    createItemElement(item) {
        const total = item.OrderCount * item.ProductPrice;

        const element = document.createElement('div');
        element.className = `inline-cart-item js-cart-product js-product-${item.ProductNr}`;
        element.dataset.productId = item.ProductNr;

        // Gift card info (same compact display as the legacy cart)
        const giftCardInfo = item.isGiftCard && item.giftCardData ? `
            <div class="gift-card-info">
                <p><strong>${t('cart.giftCard.to')}</strong> ${escapeHtml(item.giftCardData.recipientName)}${item.giftCardData.recipientEmail ? ` (${escapeHtml(item.giftCardData.recipientEmail)})` : ''}</p>
                <p><strong>${t('cart.giftCard.from')}</strong> ${escapeHtml(item.giftCardData.senderName)}</p>
                ${item.giftCardData.giftMessage ? `<p><strong>${t('cart.giftCard.message')}</strong> ${escapeHtml(item.giftCardData.giftMessage)}</p>` : ''}
                ${item.giftCardData.isPhysicalCard ? `
                    <p><strong>${t('cart.giftCard.physical')}</strong> ${t('cart.giftCard.yes')}</p>
                    ${item.giftCardData.deliveryAddress ? `
                        <p><strong>${t('cart.giftCard.deliveryAddress')}</strong><br>
                        ${escapeHtml(item.giftCardData.deliveryAddress.deliveryName)} ${escapeHtml(item.giftCardData.deliveryAddress.deliveryLastname)}<br>
                        ${escapeHtml(item.giftCardData.deliveryAddress.deliveryAddress)}<br>
                        ${escapeHtml(item.giftCardData.deliveryAddress.deliveryZip)} ${escapeHtml(item.giftCardData.deliveryAddress.deliveryCity)}</p>
                    ` : ''}
                ` : ''}
            </div>
        ` : '';

        element.innerHTML = `
            ${item.ImageUrl ? `
                <div class="inline-cart-item-image">
                    <img src="${escapeHtml(item.ImageUrl)}" alt="${escapeHtml(item.ProductName)}">
                </div>
            ` : ''}
            <div class="inline-cart-item-content">
                <a href="${escapeHtml(item.ProductUrl || '#')}" class="inline-cart-item-name">
                    ${escapeHtml(item.ProductName)}
                </a>
                ${giftCardInfo}
                <p class="inline-cart-item-id">${escapeHtml(item.ProductNr)}</p>
                <p class="inline-cart-item-price">${escapeHtml(String(item.ProductPrice))} kr</p>
                <div class="inline-cart-item-row">
                    <div class="cart-toggle-wrapper">
                        <button type="button" class="cart-decrease js-decrease" data-product-id="${escapeHtml(item.ProductNr)}">-</button>
                        <input type="number" name="amount-${escapeHtml(item.ProductNr)}" value="${item.OrderCount}" readonly aria-label="${escapeHtml(t('cart.selectQuantity'))}">
                        <button type="button" class="cart-increase js-increase" data-product-id="${escapeHtml(item.ProductNr)}">+</button>
                    </div>
                    <p class="inline-cart-item-total js-product-total">${total} kr</p>
                </div>
            </div>
        `;

        return element;
    }

    renderFooter() {
        const total = this.getCartGoodsTotal();

        if (this.isCheckout) {
            // Never show a total that could be confused with the checkout's own
            // total (which includes shipping/discount) - the label must be explicit
            this.footerElement.innerHTML = `
                <p class="inline-cart-goods-total">
                    <span>${escapeHtml(t('cart.goodsTotalExcl'))}</span>
                    <span class="js-inline-cart-total">${total.toFixed(2)} kr</span>
                </p>
                <button type="button" class="inline-cart-done js-inline-cart-done">${escapeHtml(t('cart.done'))}</button>
            `;
        } else {
            const checkoutUrl = this.system.config.checkout?.checkoutUrl || '#';
            this.footerElement.innerHTML = `
                <p class="inline-cart-goods-total">
                    <span>${escapeHtml(t('cart.total'))}</span>
                    <span class="js-inline-cart-total">${total.toFixed(2)} kr</span>
                </p>
                <a href="${escapeHtml(checkoutUrl)}" class="inline-cart-checkout-btn">${escapeHtml(t('cart.toCheckout'))}</a>
            `;
        }
    }

    getCartGoodsTotal() {
        // Same arithmetic as the legacy cart - no pricing logic changes
        return getStorage(this.system.config.storageKey).reduce((sum, item) => {
            return sum + (parseFloat(item.ProductPrice) * item.OrderCount);
        }, 0);
    }

    // ------------------------------------------------------------------
    // Quantity management (same rules as the legacy cart, incl. stock check)
    // ------------------------------------------------------------------

    increaseQuantity(productId) {
        const items = getStorage(this.system.config.storageKey);
        const index = items.findIndex(item => item.ProductNr === productId);
        if (index === -1) return;

        const product = items[index];

        const trackInventory = product.EffectiveTrackInventory === true || product.EffectiveTrackInventory === 'True';
        const allowBackorders = product.EffectiveAllowBackorders === true || product.EffectiveAllowBackorders === 'True';

        if (trackInventory && !allowBackorders) {
            const maxStock = parseInt(product.EffectiveStockQuantity || '0');
            if (product.OrderCount >= maxStock) {
                this.showStockLimitNotification(productId, maxStock);
                console.log('Max stock reached for', product.ProductName, '- cannot increase quantity');
                return;
            }
        }

        items[index].OrderCount += 1;
        setStorage(this.system.config.storageKey, items);
        this.renderItems();
        this.renderFooter();
        dispatchCartUpdatedEvent(items);
    }

    decreaseQuantity(productId) {
        const items = getStorage(this.system.config.storageKey);
        const index = items.findIndex(item => item.ProductNr === productId);
        if (index === -1) return;

        if (items[index].OrderCount > 1) {
            items[index].OrderCount -= 1;
        } else {
            items.splice(index, 1);
        }

        setStorage(this.system.config.storageKey, items);
        this.renderItems();
        this.renderFooter();
        dispatchCartUpdatedEvent(items);
    }

    emptyCart() {
        clearStorage(this.system.config.storageKey);
        this.renderItems();
        this.renderFooter();
        dispatchCartUpdatedEvent([]);
    }

    showStockLimitNotification(productId, maxStock) {
        const productElement = this.itemsContainer.querySelector(`.js-product-${CSS.escape(productId)}`);
        if (!productElement) return;

        const existing = productElement.querySelector('.stock-limit-notification');
        if (existing) existing.remove();

        const notification = document.createElement('div');
        notification.className = 'stock-limit-notification';
        notification.textContent = `Max ${maxStock} st`;

        const toggleWrapper = productElement.querySelector('.cart-toggle-wrapper');
        if (toggleWrapper) {
            toggleWrapper.parentNode.insertBefore(notification, toggleWrapper.nextSibling);
        }

        setTimeout(() => notification.remove(), 3000);
    }

    // ------------------------------------------------------------------
    // Checkout integrity: signature + reload gate
    // ------------------------------------------------------------------

    computeSignature() {
        const items = getStorage(this.system.config.storageKey);
        const pairs = items
            .map(item => [String(item.ProductNr), item.OrderCount])
            .sort((a, b) => a[0].localeCompare(b[0]));
        return JSON.stringify(pairs);
    }

    maybeReloadAfterCartChange(reason) {
        if (!this.isCheckout || this.reloadTriggered || this.pageLoadSignature === null) return;
        if (this.computeSignature() === this.pageLoadSignature) return;

        // Set the flag BEFORE navigating - makes the gate idempotent across
        // duplicate close events and pageshow firing together
        this.reloadTriggered = true;
        console.log(`InlineCart: Cart changed on checkout (${reason}) - backing up form fields and reloading`);

        this.backupFormFields();
        // location.href assignment reloads without a POST resubmit dialog
        location.href = location.pathname + location.search;
    }

    // ------------------------------------------------------------------
    // Checkout form backup / restore (whitelisted fields only)
    // ------------------------------------------------------------------

    backupFormFields() {
        const backup = {};
        FORM_FIELD_WHITELIST.forEach((id) => {
            const field = document.getElementById(id);
            if (!field) return;
            backup[id] = field.type === 'checkbox' ? field.checked : field.value;
        });

        try {
            sessionStorage.setItem(FORM_BACKUP_KEY, JSON.stringify(backup));
        } catch (e) {
            console.warn('InlineCart: Could not back up checkout form fields', e);
        }
    }

    restoreFormFields() {
        let backup = null;
        try {
            const raw = sessionStorage.getItem(FORM_BACKUP_KEY);
            if (raw) backup = JSON.parse(raw);
        } catch (e) {
            backup = null;
        }

        // Delete the key immediately - restored or not, it must not linger
        try {
            sessionStorage.removeItem(FORM_BACKUP_KEY);
        } catch (e) { /* ignore */ }

        if (!backup) return;

        Object.keys(backup).forEach((id) => {
            const field = document.getElementById(id);
            if (!field) return;
            if (field.type === 'checkbox') {
                field.checked = backup[id];
                // Show delivery fields again if "different delivery address" was checked.
                // Set display directly as well - CustomerFormHandler's change listener
                // may not be registered yet at this point
                if (id === 'differentDeliveryAddress') {
                    const deliveryFields = document.getElementById('delivery-address-fields');
                    if (deliveryFields) {
                        deliveryFields.style.display = field.checked ? '' : 'none';
                    }
                }
                field.dispatchEvent(new Event('change', { bubbles: true }));
            } else {
                field.value = backup[id];
            }
        });

        console.log('InlineCart: Restored whitelisted checkout form fields after cart-change reload');
    }
}


